import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loading } from "@/components/auth/RequireAuth";
import BasicsTab from "@/components/partner/BasicsTab";
import ItineraryTab from "@/components/partner/ItineraryTab";
import { PolicyTab, PublishTab } from "@/components/partner/PolicyPublishTabs";
import OpsTab from "@/components/partner/OpsTab";
import { useApi, errMsg } from "@/lib/api";

const TABS = [["basics", "Basics"], ["itinerary", "Timeline"], ["policy", "Policy & payout"], ["publish", "Compliance"], ["roster", "Roster & waitlist"], ["broadcasts", "Broadcasts"], ["completion", "Completion"]];

export default function PartnerListing() {
  const { id } = useParams();
  const api = useApi();
  const [data, setData] = useState(null);
  const [v, setV] = useState(0);
  const load = useCallback(() => api.get(`/partner/listings/${id}`).then((r) => { setData(r.data); setV((x) => x + 1); }).catch((e) => toast.error(errMsg(e))), [api, id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const l = data.listing;
  const props = { data, api, reload: load };
  return (
    <div className="mx-auto max-w-7xl px-5 py-10 md:px-8" data-testid="partner-listing-page">
      <Link to="/partner" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"><ArrowLeft className="h-4 w-4" />Workspace</Link>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <h1 className="text-3xl tracking-tight text-primary sm:text-4xl" data-testid="builder-title">{l.title}</h1>
        <Badge variant="outline" className="capitalize" data-testid="builder-status">{l.status.replace("_", " ")}</Badge>
        {l.status === "published" && <Link to={`/l/${l.slug}`} className="inline-flex items-center gap-1 text-sm text-secondary"><ExternalLink className="h-4 w-4" />View live</Link>}
      </div>
      <Tabs defaultValue="basics" className="mt-8">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
          {TABS.map(([k, label]) => <TabsTrigger key={k} value={k} className="rounded-full px-4 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`builder-tab-${k}`}>{label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="basics" className="mt-8"><BasicsTab key={v} {...props} /></TabsContent>
        <TabsContent value="itinerary" className="mt-8"><ItineraryTab key={v} {...props} /></TabsContent>
        <TabsContent value="policy" className="mt-8"><PolicyTab key={v} {...props} /></TabsContent>
        <TabsContent value="publish" className="mt-8"><PublishTab {...props} /></TabsContent>
        <TabsContent value="roster" className="mt-8"><OpsTab data={data} api={api} section="roster" /></TabsContent>
        <TabsContent value="broadcasts" className="mt-8"><OpsTab data={data} api={api} section="broadcasts" /></TabsContent>
        <TabsContent value="completion" className="mt-8"><OpsTab key={v} data={data} api={api} section="completion" /></TabsContent>
      </Tabs>
    </div>
  );
}
